import apiClient from '../lib/api-client';

export interface UploadFileResponse {
  success: boolean;
  message: string;
  file: {
    id: string;
    filename: string;
    originalname: string;
    url: string;
    size: number;
    mimetype: string;
    file_type: 'image' | 'video' | 'audio' | 'document' | 'other';
    metadata?: {
      width?: number;
      height?: number;
      format?: string;
      [key: string]: any;
    };
  };
}

export interface UploadMultipleFilesResponse {
  success: boolean;
  message: string;
  files: UploadFileResponse['file'][];
}

export interface FileMetadata {
  id: string;
  filename: string;
  originalname: string;
  url: string;
  size: number;
  mimetype: string;
  file_type: 'image' | 'video' | 'audio' | 'document' | 'other';
  entity_type?: 'player' | 'coach' | 'club' | 'post' | 'match' | 'other';
  entity_id?: string;
  metadata?: any;
  created_at: string;
}

class FileService {
  /**
   * Upload un fichier unique
   */
  async uploadFile(
    file: File,
    options?: {
      entity_type?: string;
      entity_id?: string;
    }
  ): Promise<UploadFileResponse['file']> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (options?.entity_type) {
      formData.append('entity_type', options.entity_type);
    }
    if (options?.entity_id) {
      formData.append('entity_id', options.entity_id);
    }

    const response = await apiClient.post<UploadFileResponse>(
      '/files/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data.file;
  }

  /**
   * Upload une photo de profil
   */
  async uploadProfilePicture(
    file: File,
    options?: {
      entity_id?: string;
    }
  ): Promise<UploadFileResponse['file']> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', 'player');
    
    if (options?.entity_id) {
      formData.append('entity_id', options.entity_id);
    }

    const response = await apiClient.post<UploadFileResponse>(
      '/files/upload/profile-picture',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data.file;
  }

  /**
   * Upload une photo de couverture
   */
  async uploadCoverPhoto(
    file: File,
    options?: {
      entity_id?: string;
    }
  ): Promise<UploadFileResponse['file']> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', 'player');
    
    if (options?.entity_id) {
      formData.append('entity_id', options.entity_id);
    }

    const response = await apiClient.post<UploadFileResponse>(
      '/files/upload/cover-photo',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data.file;
  }

  /**
   * Upload plusieurs fichiers
   */
  async uploadMultipleFiles(
    files: File[],
    options?: {
      entity_type?: string;
      entity_id?: string;
    }
  ): Promise<UploadMultipleFilesResponse['files']> {
    const formData = new FormData();
    
    files.forEach((file) => {
      formData.append('files', file);
    });
    
    if (options?.entity_type) {
      formData.append('entity_type', options.entity_type);
    }
    if (options?.entity_id) {
      formData.append('entity_id', options.entity_id);
    }

    const response = await apiClient.post<UploadMultipleFilesResponse>(
      '/files/upload-multiple',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data.files;
  }

  /**
   * Upload des médias pour un post (images/vidéos)
   */
  async uploadPostMedia(
    files: File[],
    options?: {
      entity_id?: string;
    }
  ): Promise<UploadMultipleFilesResponse['files']> {
    const formData = new FormData();
    
    files.forEach((file) => {
      formData.append('files', file);
    });
    
    formData.append('entity_type', 'post');
    
    if (options?.entity_id) {
      formData.append('entity_id', options.entity_id);
    }

    const response = await apiClient.post<UploadMultipleFilesResponse>(
      '/files/upload/post',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data.files;
  }

  /**
   * Récupérer les métadonnées d'un fichier
   */
  async getFileMetadata(filename: string): Promise<FileMetadata> {
    const response = await apiClient.get<{ success: boolean; file: FileMetadata }>(
      `/files/metadata/${filename}`
    );
    return response.data.file;
  }

  /**
   * Supprimer un fichier
   */
  async deleteFile(filename: string): Promise<void> {
    await apiClient.delete(`/files/${filename}`);
  }

  /**
   * Lister les fichiers
   */
  async listFiles(filters?: {
    entity_type?: string;
    entity_id?: string;
    file_type?: string;
    uploaded_by?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    files: FileMetadata[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const response = await apiClient.get<{
      success: boolean;
      files: FileMetadata[];
      pagination: any;
    }>('/files/list', { params: filters });
    
    return {
      files: response.data.files,
      pagination: response.data.pagination,
    };
  }
}

export const fileService = new FileService();
